// @ai-generated 2026-08-21T00:00:00Z by niklas.eicker@tngtech.com using Claude Opus 5 (1M context)
import { useSession } from '@Contexts/session';
import type { ReactToolCallRenderer } from '@copilotkit/react-core/v2';
import { concatUrlSegments } from '@utils/concatUrlSegments';
import classes from './ChatEntityToolCallCard.module.css';
import { ENTITY_TOOLS, type Column, type EntityTool } from './entityTools';
import { ToolCallCardShell } from './ToolCallCardShell';
import { readEntityPage, type EntityPage } from '@utils/toolCallEnvelope';
import { resolveToolCallState } from './toolCallStatus';

function EntityToolCallCard({
    tool,
    args,
    result,
    status,
}: {
    tool: EntityTool;
    args: unknown;
    result: unknown;
    status: unknown;
}) {
    const {
        current: { subpath },
    } = useSession();
    const state = resolveToolCallState(status, result);
    const page = state.isRunning || state.isFailed ? undefined : readEntityPage(result);

    // Only a payload we could read is summarized; anything else falls back to what was asked
    // for, so a result we failed to interpret never reads as "nothing came back".
    const summary = page === undefined ? tool.describeArgs(args) : tool.summarize(page);

    return (
        <ToolCallCardShell
            title={tool.title}
            summary={summary}
            state={state}
            result={result}
            {...(page !== undefined && page.items.length > 0 ? { body: renderTable(page, tool, subpath) } : {})}
        />
    );
}

function renderTable(page: EntityPage, tool: EntityTool, subpath: string) {
    return (
        <table className={classes.table}>
            <thead>
                <tr>
                    {tool.columns.map((column) => (
                        <th key={column.label} className={cellClass(classes.headerCell, column)}>
                            {column.label}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {page.items.map((item, index) => (
                    <tr key={typeof item.id === 'string' ? item.id : index}>
                        {tool.columns.map((column, columnIndex) => (
                            <td key={column.label} className={cellClass(classes.cell, column)}>
                                {columnIndex === 0 ? renderIdentifier(item, tool, subpath) : column.value(item) ?? '—'}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

/** Numbers read right-aligned, so a column says which of the two it is. */
function cellClass(base: string, column: Column): string {
    return column.numeric ? `${base} ${classes.numeric}` : base;
}

/**
 * The leading cell, linked to the record in the ERP when the hit carries an id.
 *
 * Opens in a new tab on purpose: the chat mints a fresh thread on every mount, so navigating the
 * current tab away would discard the conversation the reader is following. Once a thread survives
 * a reload this can become an in-app navigation.
 */
function renderIdentifier(item: Record<string, unknown>, tool: EntityTool, subpath: string) {
    const label = tool.columns[0].value(item) ?? '—';
    const id = typeof item.id === 'string' && item.id !== '' ? item.id : undefined;
    if (id === undefined) {
        return label;
    }

    return (
        <a
            href={concatUrlSegments('/', subpath, tool.detailPath(id))}
            target="_blank"
            rel="noreferrer"
            className={classes.link}
        >
            {label}
        </a>
    );
}

/**
 * One renderer per record-listing tool; spread these before the wildcard fallback.
 *
 * Declared as plain renderers rather than via `defineToolCallRenderer`, which only types a named
 * tool together with a Standard Schema for its arguments. The cards read arguments and results
 * defensively anyway, so a schema would buy nothing but a zod dependency.
 */
export const ChatEntityToolCallCards: ReactToolCallRenderer[] = ENTITY_TOOLS.map((tool) => ({
    name: tool.name,
    render: ({ args, result, status }) => (
        <EntityToolCallCard tool={tool} args={args} result={result} status={status} />
    ),
}));
